import { useCallback, useMemo, useState } from 'react';
import { SaveStatus, type PrescriptionSavingStatus } from '../useSavePrescriptions/types';

const mergeStatuses = (prev: PrescriptionSavingStatus[], newStatuses: PrescriptionSavingStatus[]) => {
  const prevFieldIds = new Set(prev.map((status) => status.fieldId));
  const newStatusMap = new Map(newStatuses.map((status) => [status.fieldId, status]));

  const updatedStatuses = prev.map((prevStatus) => {
    const matchingNewStatus = newStatusMap.get(prevStatus.fieldId);
    return matchingNewStatus ? { ...prevStatus, ...matchingNewStatus } : prevStatus;
  });

  const notIncludedStatuses = newStatuses.filter((newStatus) => !prevFieldIds.has(newStatus.fieldId));

  return updatedStatuses.concat(notIncludedStatuses);
};

export const useSavingStatus = () => {
  const [prescriptionSaveStatuses, setPrescriptionSaveStatuses] = useState<PrescriptionSavingStatus[]>([]);

  const updatePrescriptionStatuses = useCallback((newStatuses: PrescriptionSavingStatus[]) => {
    setPrescriptionSaveStatuses((prev) => mergeStatuses(prev, newStatuses));
  }, []);

  const resetPrescriptionStatuses = useCallback(() => {
    setPrescriptionSaveStatuses([]);
  }, []);

  const isSaving = useMemo(
    () => prescriptionSaveStatuses.some(({ status }) => status === SaveStatus.PENDING),
    [prescriptionSaveStatuses]
  );

  const allSaved = useMemo(
    () => prescriptionSaveStatuses.every(({ status }) => status === SaveStatus.SUCCEEDED)
    && prescriptionSaveStatuses.length > 0,
    [prescriptionSaveStatuses]
  );

  const areErrors = useMemo(
    () => prescriptionSaveStatuses.some(({ status }) => status === SaveStatus.FAILED),
    [prescriptionSaveStatuses]
  );

  const allFailed = useMemo(
    () => prescriptionSaveStatuses.every(({ status }) => status === SaveStatus.FAILED)
    && prescriptionSaveStatuses.length > 0,
    [prescriptionSaveStatuses]
  );

  return {
    allFailed,
    allSaved,
    areErrors,
    isSaving,
    prescriptionSaveStatuses,
    resetPrescriptionStatuses,
    updatePrescriptionStatuses
  };
};
